import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  Center,
  Spinner,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useMediaQuery,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';

const Roles = ({ isAuthenticated, onNotAuthenticated }) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const toast = useToast();
  const [isMobile] = useMediaQuery('(max-width: 768px)');

  // Fetch roles on mount
  const fetchRoles = async () => {
    try {
      setLoading(true);
      const baseApiUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:8000';
      const response = await fetch(`${baseApiUrl}/users.php?type=roles`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 401) {
        onNotAuthenticated();
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch roles');
      }

      const data = await response.json();
      setRoles(Array.isArray(data.roles) ? data.roles : []);
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchRoles();
    }
  }, [isAuthenticated]);

  const handleAddRole = () => {
    setFormData({ name: '', description: '' });
    setEditingId(null);
    onOpen();
  };

  const handleEdit = (role) => {
    setFormData({ name: role.name, description: role.description || '' });
    setEditingId(role.id);
    onOpen();
  };

  const handleModalClose = () => {
    setFormData({ name: '', description: '' });
    setEditingId(null);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a role name',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const method = editingId ? 'PUT' : 'POST';
      const payload = editingId
        ? { ...formData, id: editingId }
        : formData;

      const baseApiUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:8000';
      const response = await fetch(`${baseApiUrl}/roles.php`, {
        method,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 401) {
        onNotAuthenticated();
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save role');
      }

      const responseData = await response.json();

      toast({
        title: 'Success',
        description: editingId ? 'Role updated successfully' : 'Role created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Update local state instead of refetching to avoid screen flash
      if (editingId) {
        // Update existing role
        setRoles(roles.map(r => r.id === editingId ? responseData.role : r));
      } else {
        // Add new role
        setRoles([...roles, responseData.role]);
      }

      setFormData({ name: '', description: '' });
      setEditingId(null);
      onClose();
    } catch (error) {
      console.error('Error saving role:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeleteClick = (role) => {
    setDeleteTarget(role);
    onDeleteOpen();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      const baseApiUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:8000';
      const response = await fetch(`${baseApiUrl}/roles.php`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: deleteTarget.id }),
      });

      if (response.status === 401) {
        onNotAuthenticated();
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete role');
      }

      toast({
        title: 'Success',
        description: 'Role deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Remove role from local state instead of refetching to avoid screen flash
      setRoles(roles.filter(r => r.id !== deleteTarget.id));
      setDeleteTarget(null);
      onDeleteClose();
    } catch (error) {
      console.error('Error deleting role:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Table columns
  const columns = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: info => info.getValue(),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: info => info.getValue() || '—',
    },
    {
      id: 'actions',
      header: '',
      cell: (info) => {
        const role = info.row.original;
        const isProtected = role.name.toLowerCase() === 'admin' || role.name.toLowerCase() === 'volunteer';
        
        return (
          <HStack spacing={2}>
            <IconButton
              icon={<EditIcon />}
              size="sm"
              variant="ghost"
              colorScheme="blue"
              onClick={() => handleEdit(role)}
              isDisabled={isProtected}
              title={isProtected ? 'Cannot edit protected roles' : 'Edit role'}
              aria-label="Edit role"
            />
            <IconButton
              icon={<DeleteIcon />}
              size="sm"
              variant="ghost"
              colorScheme="red"
              onClick={() => handleDeleteClick(role)}
              isDisabled={isProtected}
              title={isProtected ? 'Cannot delete protected roles' : 'Delete role'}
              aria-label="Delete role"
            />
          </HStack>
        );
      },
    },
  ];

  const table = useReactTable({
    data: roles,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Box minH="100vh" bg="linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)">
      <Container maxW={isMobile ? "100%" : "6xl"} px={isMobile ? 4 : 6} py={8}>
        <VStack align="start" spacing={6} mb={8}>
          {/* Header */}
          <HStack 
            justify="space-between" 
            width="100%" 
            flexDirection={isMobile ? "column" : "row"} 
            align={isMobile ? "start" : "center"} 
            spacing={isMobile ? 3 : 0}
          >
            <Heading 
              as="h1" 
              size={isMobile ? "lg" : "2xl"} 
              color="gray.900" 
              fontWeight="700"
            >
              Manage Roles
            </Heading>
            <Button
              leftIcon={<AddIcon />}
              colorScheme="blue"
              size={isMobile ? "sm" : "md"}
              onClick={handleAddRole}
            >
              Add Role
            </Button>
          </HStack>
        </VStack>

        {/* Loading State */}
        {loading ? (
          <Center py={10}>
            <Spinner size="lg" color="blue.500" />
          </Center>
        ) : roles.length === 0 ? (
          <Center py={10}>
            <Text color="gray.500">No roles found</Text>
          </Center>
        ) : (
          /* Roles Table */
          <Box 
            bg="white" 
            borderRadius="lg" 
            boxShadow="md" 
            overflowX="auto"
          >
            <Table variant="simple" size={{ base: 'sm', md: 'md' }}>
              <Thead bg="gray.100">
                {table.getHeaderGroups().map(headerGroup => (
                  <Tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <Th
                        key={header.id}
                        cursor={header.column.getCanSort() ? 'pointer' : 'default'}
                        onClick={header.column.getToggleSortingHandler()}
                        color="gray.700"
                        fontWeight="600"
                        fontSize={isMobile ? "xs" : "sm"}
                      >
                        <HStack spacing={1}>
                          <Text>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </Text>
                          {header.column.getCanSort() && (
                            <Text fontSize="xs">
                              {header.column.getIsSorted()
                                ? header.column.getIsSorted() === 'desc'
                                  ? ' 🔽'
                                  : ' 🔼'
                                : ''}
                            </Text>
                          )}
                        </HStack>
                      </Th>
                    ))}
                  </Tr>
                ))}
              </Thead>
              <Tbody>
                {table.getRowModel().rows.map(row => (
                  <Tr key={row.id} _hover={{ bg: 'gray.50' }}>
                    {row.getVisibleCells().map(cell => (
                      <Td key={cell.id} fontSize={isMobile ? "xs" : "sm"}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </Container>

      {/* Add/Edit Role Modal */}
      <Modal isOpen={isOpen} onClose={handleModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingId ? 'Edit Role' : 'Add New Role'}
          </ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Name</FormLabel>
                  <Input
                    type="text"
                    placeholder="e.g., admin, editor, viewer"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    placeholder="What is this role for?"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    rows={3}
                  />
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={handleModalClose}>
                Cancel
              </Button>
              <Button colorScheme="blue" type="submit">
                {editingId ? 'Update' : 'Create'}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        leastDestructiveRef={undefined}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Role
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete the role{' '}
              <strong>{deleteTarget?.name}</strong>? This action cannot be
              undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default Roles;
